import { type ComponentProps, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "./HealthcareProviderExcerptOverview.styles";
import ConfirmRevoke from "src/components/organisms/ConfirmRevoke/ConfirmRevoke";
import RevokeResultsDialog from "src/components/organisms/RevokeResultsDialog/RevokeResultsDialog";
import {
    GetIssuedCredentialsDocument,
    useGetIssuedCredentialsQuery,
    useRevokeHealthcareProviderExcerptCredentialMutation,
} from "@/src/graphql/generated/api/graphql";
import HealthcareProvidersTable from "@/src/components/organisms/HealthcareProvidersTable/HealthcareProvidersTable";
import { useDebounce } from "@/src/lib/use-debounce/use-debounce";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type HealthcareProviderDataSummary = {
    id: string;
    name: string;
    cocNumber: string;
    did: string;
};

const HealthcareProviderExcerptOverview = () => {
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 300);

    const [toRevoke, setToRevoke] = useState<HealthcareProviderDataSummary[]>(
        []
    );

    const [confirmedRevoke, setConfirmedRevoke] = useState<
        HealthcareProviderDataSummary[]
    >([]);

    const [revokeResults, setRevokeResults] =
        useState<ComponentProps<typeof RevokeResultsDialog>["results"]>();

    const { t } = useTranslation("common", {
        keyPrefix: "healthcare-provider-excerpts-overview",
    });

    const [page, setPage] = useState(0);

    const [pageSize, setPageSize] = useState(15);

    const { loading, data } = useGetIssuedCredentialsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
            },
            search: debouncedQuery,
        },
        // Show credentials issued since the last visit
        fetchPolicy: "cache-and-network",
    });

    // Total count
    const count = data?.getIssuedHealthcareProviderExcerptCredentials.rowCount;

    // flatmap to get the actual healthcare provider data
    const healthcareProviderDataSummaries =
        data?.getIssuedHealthcareProviderExcerptCredentials.rows.map(
            ({ id, healthcareProvider }) => ({
                ...healthcareProvider,
                id,
                name: healthcareProvider.name,
                cocNumber: healthcareProvider.cocNumber,
                did: healthcareProvider.did,
            })
        ) as HealthcareProviderDataSummary[];

    const onFilter = (query: string) => {
        setQuery(query);
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPageSize(Number(event.target.value));
    };

    const handleDeleteClick = (providerId: string) => {
        const item =
            healthcareProviderDataSummaries?.filter(
                (item) => item.id === providerId
            ) ?? [];

        setToRevoke(item);
    };

    const handleRevokeConfirmClose = ({
        confirmed,
    }: {
        confirmed?: boolean | null;
    }) => {
        if (confirmed) setConfirmedRevoke(toRevoke);
        setToRevoke([]);
    };

    const handleRevokeResultsDialogOnClose = () => {
        setRevokeResults(undefined);
    };

    const [revoke, { loading: revocationLoading }] =
        useRevokeHealthcareProviderExcerptCredentialMutation({
            variables: {
                ids: confirmedRevoke.map(({ id }) => id),
            },
            onCompleted: () => {
                setConfirmedRevoke([]);
            },
            onError: (error) => {
                console.error(error);
                setConfirmedRevoke([]);
            },
            refetchQueries: [GetIssuedCredentialsDocument],
        });

    useEffect(() => {
        if (confirmedRevoke.length) {
            revoke().then((response) => {
                const revokeResults =
                    response.data?.revokeHealthcareProviderExcerptCredential;
                setRevokeResults(revokeResults);
            });
        }
    }, [confirmedRevoke]);

    const { hasPermission } = usePermissions();

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={t("title")}
                    {...(hasPermission(
                        "HEALTHCARE_PROVIDER_EXCERPT_CREDENTIAL_CREATE"
                    ) && {
                        button: {
                            to: "new",
                            label: t("healthcare-provider-excerpts"),
                        },
                    })}
                />
                <styled.Main>
                    <styled.FilterControl
                        label={t("filter")}
                        onChange={(event) => onFilter(event.target.value)}
                    />
                    <HealthcareProvidersTable
                        providers={healthcareProviderDataSummaries}
                        loading={loading}
                        onDeleteClick={handleDeleteClick}
                        page={page}
                        rowsPerPage={pageSize}
                        totalCount={count || 0}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </styled.Main>
            </styled.Container>
            <ConfirmRevoke
                open={revocationLoading || Boolean(toRevoke.length)}
                loading={revocationLoading}
                indicators={toRevoke}
                onClose={handleRevokeConfirmClose}
            />
            <RevokeResultsDialog
                open={Boolean(revokeResults?.length)}
                results={revokeResults}
                onClose={handleRevokeResultsDialogOnClose}
                navigateToPath="/healthcare-provider-excerpts"
            />
        </>
    );
};

export default HealthcareProviderExcerptOverview;
